import { ref, computed, watch } from '@vue/composition-api'
// import { title } from '@core/utils/filter'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import moment from 'moment-timezone'
import store from '@/store'
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import useFilterCounter from '@/components/Filters/useFiltersCount'
import { debounce } from 'lodash'
import ability from '@/libs/acl/ability'

export default function useList() {
  const { t } = useI18nUtils();
  const trans = {
    'messages.error.server_error': t('messages.error.server_error')
  };
  const refListTable = ref(null)

  const toast = useToast()

  // ==================== Tables

  const tableColumns = [
    { label: 'pbx_device.name', key: 'name', sortable: true },
    { label: 'pbx_device.extension', key: 'extension', sortable: true },
    { label: 'pbx_device.password', key: 'password', sortable: true },
    { label: 'centers.title', key: 'id_center', sortable: true, formatter: 'setCenterName' },
    { label: 'sectors.title', key: 'id_sector', sortable: true, formatter: 'setSectorName' },
    { label: 'pbx_master_account.title', key: 'id_master_account', sortable: true, formatter: 'setMasterAccountName' },
    { label: 'unit.title', key: 'id_unit', sortable: true, formatter: 'setUnitsName' },
    { label: 'users.title', key: 'id_user', sortable: true, formatter: 'setUserName' },
    { label: 'pbx_device.ha1', key: 'ha1', sortable: true },
    { label: 'pbx_device.dispatch_alarm', key: 'dispatch_alarm', sortable: true },
    { label: 'pbx_device.ivr', key: 'ivr', sortable: false },
    {
      label: 'date.created_at', key: 'created_at', sortable: true, formatter: 'formatDateAssigned',
    },
    {
      label: 'date.updated_at', key: 'updated_at', sortable: true, formatter: 'formatDateAssigned',
    },
  ]

  if (ability.can('update', 'PbxDevice') || ability.can('delete', 'PbxDevice')) {
    tableColumns.splice(0, 0, { label: 'actions.name', key: 'actions' })
  }

  const perPage = ref(10)
  const totalList = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    }
  })
  // ========== Getters Filters
  store.dispatch('sector/getAll')
  const sectorOptions = computed(() => store.getters['sector/getSelectedItems'])
  const sectorFilter = ref(null)

  store.dispatch('center/getAll')
  const centerOptions = computed(() => store.getters['center/getSelectedItems'])
  const centerFilter = ref(null)

  store.dispatch('tenant/getAll')
  const tenantOptions = computed(() => store.getters['tenant/getSelectedItems'])
  const tenantFilter = ref(null)

  store.dispatch('unit/getAll', { with: 'router:id,id_tenant' })
  const unitOptions = computed(() => store.getters['unit/getSelectedItems'])

  store.dispatch('camera_type/getAll')
  const cameraTypeOptions = computed(() => store.getters['camera_type/getSelectedItems'])

  store.dispatch('pbx_master_account/getAll')
  const masterAccountOptions = computed(() => store.getters['pbx_master_account/getSelectedItems'])
  const masterAccountFilter = ref(null)

  store.dispatch('omniview_reseller/getAll');
  const resellerOptions = computed(() => store.getters['omniview_reseller/getSelectedItems'])
  const resellerFilter = ref(null)

  store.dispatch('user/getAll', { withoutConfig: true, withPermissions: false })
  const userOptions = computed(() => store.getters['user/getSelectedItems'])
  const userFilter = ref(null)

  const onlineFilter = ref(null)

  const fetchList = (ctx, callback) => {
    store.dispatch('pbx_device/getAll', {
      q: searchQuery.value,
      pageSize: perPage.value,
      page: currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      id_sector: sectorFilter.value,
      id_center: centerFilter.value,
      id_master_account: masterAccountFilter.value,
      id_user: userFilter.value,
      online: onlineFilter.value
    })
    .then(response => {
      callback(response.data)
      totalList.value = response.meta.totalItems
    })
    .catch(() => {
      toast({
        component: ToastificationContent,
        props: {
          title: trans['messages.error.server_error'],
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    })
  }

  const buttonUpdate = ref(false);
  const loadingToast = ref(null);

  const formatDateAssigned = value => {
    const formattedDate = moment(value).format('DD/MM/YYYY HH:mm:ss')
    return formattedDate
  }

  const refetchData = () => {
    refListTable.value.refresh()
  }

  watch([
    currentPage,
    perPage,
  ], () => { refetchData() })

  watch([
    searchQuery
  ], debounce(() => {
    refetchData()
    }, 500))

  // filters counter ============================
  const filters = ref({});
  const filtersCount = useFilterCounter(filters);
  const buildFilters = () => {
    filters.value = {
      id_center: centerFilter.value,
      id_sector: sectorFilter.value,
      id_master_account: masterAccountFilter.value,
      online: onlineFilter.value,
    };
    refetchData();
  }
  const clearSearchFilters = () => {
    centerFilter.value = null;
    sectorFilter.value = null;
    masterAccountFilter.value = null,
    onlineFilter.value = null;
    resellerFilter.value = null;
    buildFilters();
    filters.value = ({});
  }
  // ============================================

  const setCenterName = value => {
    let center = centerOptions.value.find(center => center.value == value)
    return center?.label ?? ''
  }

  const setSectorName = value => {
    let sector = sectorOptions.value.find(sector => sector.value == value)
    return sector?.label ?? ''
  }

  const setMasterAccountName = value => {
    let masterAccount = masterAccountOptions.value.find(masterAccount => masterAccount.value == value)
    return masterAccount?.label ?? ''
  }

  const setUserName = value => {
    let user = userOptions.value.find(user => user.value == value)
    return user?.label ?? ''
  }

  const setUnitsName = value => {
    let unit = unitOptions.value.find(unit => unit.value == value)
    return unit?.label ?? ''
  }

  return {
    tenantOptions,
    tenantFilter,

    currentPage,
    dataMeta,
    isSortDirDesc,
    perPage,
    perPageOptions,
    searchQuery,
    sortBy,
    tableColumns,
    totalList,

    fetchList,
    buttonUpdate,
    loadingToast,
    refListTable,
    formatDateAssigned,

    centerFilter,
    onlineFilter,
    sectorFilter,
    masterAccountFilter,
    cameraTypeOptions,
    centerOptions,
    sectorOptions,
    unitOptions,
    masterAccountOptions,

    resellerOptions,
    resellerFilter,

    userOptions,
    userFilter,

    refetchData,

    filtersCount,
    buildFilters,
    clearSearchFilters,

    setCenterName, 
    setSectorName,
    setMasterAccountName,
    setUnitsName,
    setUserName
  }
}
